import React from 'react';
import { StatRow } from './StatRow';
import { Stats } from './types';

interface SectionProps {
  stats: Stats;
  homeId: string;
  awayId: string;
}

export const GeneralSection = ({ stats, homeId, awayId }: SectionProps) => (
  <div className="mb-4 px-2">
    <StatRow label="Goals" homeValue={stats.general[homeId]?.goals} awayValue={stats.general[awayId]?.goals} />
    <StatRow label="xG" homeValue={stats.general[homeId]?.xg} awayValue={stats.general[awayId]?.xg} format="decimal" />
    <StatRow label="Shots" homeValue={stats.general[homeId]?.shots} awayValue={stats.general[awayId]?.shots} />
    <StatRow label="On Target" homeValue={stats.general[homeId]?.shotsOnTarget} awayValue={stats.general[awayId]?.shotsOnTarget} />
    <StatRow label="Shots from Box" homeValue={stats.general[homeId]?.shotsFromBox} awayValue={stats.general[awayId]?.shotsFromBox} />
    <StatRow label="Blocked Shots" homeValue={stats.general[homeId]?.shotsBlocked} awayValue={stats.general[awayId]?.shotsBlocked} />
    <StatRow label="Corners" homeValue={stats.general[homeId]?.corners} awayValue={stats.general[awayId]?.corners} />
    <StatRow label="Fouls" homeValue={stats.general[homeId]?.fouls} awayValue={stats.general[awayId]?.fouls} higherIsBetter={false} />
    <StatRow label="Yellow Cards" homeValue={stats.general[homeId]?.yellowCards} awayValue={stats.general[awayId]?.yellowCards} higherIsBetter={false} />
    <StatRow label="Red Cards" homeValue={stats.general[homeId]?.redCards} awayValue={stats.general[awayId]?.redCards} higherIsBetter={false} />
    <StatRow label="Offsides" homeValue={stats.general[homeId]?.offsides} awayValue={stats.general[awayId]?.offsides} />
    <StatRow label="Progressive Runs" homeValue={stats.general[homeId]?.progressiveRuns} awayValue={stats.general[awayId]?.progressiveRuns} />
    <StatRow label="Touches in Box" homeValue={stats.general[homeId]?.touchesInBox} awayValue={stats.general[awayId]?.touchesInBox} />
  </div>
);

export const PossessionSection = ({ stats, homeId, awayId }: SectionProps) => (
  <div className="mb-4 px-2">
    <StatRow
      label="Possession %"
      homeValue={stats.possession[homeId]?.possessionPercent}
      awayValue={stats.possession[awayId]?.possessionPercent}
      format="percent"
    />
    <StatRow
      label="Possession Time"
      homeValue={stats.possession[homeId]?.purePossessionTime}
      awayValue={stats.possession[awayId]?.purePossessionTime}
      format="string"
    />
    <StatRow
      label="Possessions"
      homeValue={stats.possession[homeId]?.possessionNumber}
      awayValue={stats.possession[awayId]?.possessionNumber}
    />
    <StatRow
      label="Avg Duration"
      homeValue={stats.possession[homeId]?.avgPossessionDuration}
      awayValue={stats.possession[awayId]?.avgPossessionDuration}
      format="string"
    />
    <StatRow
      label="Reached Opp. Half"
      homeValue={stats.possession[homeId]?.reachingOpponentHalf}
      awayValue={stats.possession[awayId]?.reachingOpponentHalf}
    />
    <StatRow
      label="Reached Opp. Box"
      homeValue={stats.possession[homeId]?.reachingOpponentBox}
      awayValue={stats.possession[awayId]?.reachingOpponentBox}
    />
  </div>
);

export const PassingSection = ({ stats, homeId, awayId }: SectionProps) => (
  <div className="mb-4 px-2">
    <StatRow
      label="Total Passes"
      homeValue={stats.passes[homeId]?.passes}
      awayValue={stats.passes[awayId]?.passes}
    />
    <StatRow
      label="Successful"
      homeValue={stats.passes[homeId]?.passesSuccessful}
      awayValue={stats.passes[awayId]?.passesSuccessful}
    />
    <StatRow
      label="Pass Accuracy"
      homeValue={stats.passes[homeId]?.passes ? (stats.passes[homeId].passesSuccessful / stats.passes[homeId].passes * 100) : undefined}
      awayValue={stats.passes[awayId]?.passes ? (stats.passes[awayId].passesSuccessful / stats.passes[awayId].passes * 100) : undefined}
      format="percent"
    />
    <StatRow
      label="Long Passes"
      homeValue={stats.passes[homeId]?.longPasses}
      awayValue={stats.passes[awayId]?.longPasses}
    />
    <StatRow
      label="Long Success"
      homeValue={stats.passes[homeId]?.longPassesSuccessful}
      awayValue={stats.passes[awayId]?.longPassesSuccessful}
    />
    <StatRow
      label="Crosses"
      homeValue={stats.passes[homeId]?.crossesTotal}
      awayValue={stats.passes[awayId]?.crossesTotal}
    />
    <StatRow
      label="Crosses Success"
      homeValue={stats.passes[homeId]?.crossesSuccessful}
      awayValue={stats.passes[awayId]?.crossesSuccessful}
    />
    <StatRow
      label="Key Passes"
      homeValue={stats.passes[homeId]?.keyPasses}
      awayValue={stats.passes[awayId]?.keyPasses}
    />
    <StatRow
      label="Assists"
      homeValue={stats.passes[homeId]?.assists}
      awayValue={stats.passes[awayId]?.assists}
    />
    <StatRow
      label="Progressive Passes"
      homeValue={stats.passes[homeId]?.progressivePasses}
      awayValue={stats.passes[awayId]?.progressivePasses}
    />
    <StatRow
      label="Through Balls"
      homeValue={stats.passes[homeId]?.throughPasses}
      awayValue={stats.passes[awayId]?.throughPasses}
    />
    <StatRow
      label="Avg Pass Length"
      homeValue={stats.passes[homeId]?.avgPassLength}
      awayValue={stats.passes[awayId]?.avgPassLength}
      format="decimal"
    />
  </div>
);

export const DefenceSection = ({ stats, homeId, awayId }: SectionProps) => (
  <div className="mb-4 px-2">
    <StatRow
      label="Tackles"
      homeValue={stats.defence[homeId]?.tackles}
      awayValue={stats.defence[awayId]?.tackles}
    />
    <StatRow
      label="Interceptions"
      homeValue={stats.defence[homeId]?.interceptions}
      awayValue={stats.defence[awayId]?.interceptions}
    />
    <StatRow
      label="Clearances"
      homeValue={stats.defence[homeId]?.clearances}
      awayValue={stats.defence[awayId]?.clearances}
    />
    <StatRow
      label="PPDA"
      homeValue={stats.defence[homeId]?.ppda}
      awayValue={stats.defence[awayId]?.ppda}
      format="decimal"
      higherIsBetter={false}
    />
  </div>
);

export const DuelsSection = ({ stats, homeId, awayId }: SectionProps) => (
  <div className="mb-4 px-2">
    <StatRow
      label="Total Duels"
      homeValue={stats.duels[homeId]?.duels}
      awayValue={stats.duels[awayId]?.duels}
    />
    <StatRow
      label="Duels Won"
      homeValue={stats.duels[homeId]?.duelsSuccessful}
      awayValue={stats.duels[awayId]?.duelsSuccessful}
    />
    <StatRow
      label="Win Rate"
      homeValue={stats.duels[homeId]?.duels ? (stats.duels[homeId].duelsSuccessful / stats.duels[homeId].duels * 100) : undefined}
      awayValue={stats.duels[awayId]?.duels ? (stats.duels[awayId].duelsSuccessful / stats.duels[awayId].duels * 100) : undefined}
      format="percent"
    />
    <StatRow
      label="Aerial Duels"
      homeValue={stats.duels[homeId]?.aerialDuels}
      awayValue={stats.duels[awayId]?.aerialDuels}
    />
    <StatRow
      label="Aerial Won"
      homeValue={stats.duels[homeId]?.aerialDuelsSuccessful}
      awayValue={stats.duels[awayId]?.aerialDuelsSuccessful}
    />
    <StatRow
      label="Ground Duels"
      homeValue={stats.duels[homeId]?.groundDuels}
      awayValue={stats.duels[awayId]?.groundDuels}
    />
    <StatRow
      label="Ground Won"
      homeValue={stats.duels[homeId]?.groundDuelsSuccessful}
      awayValue={stats.duels[awayId]?.groundDuelsSuccessful}
    />
    <StatRow
      label="Dribbles"
      homeValue={stats.duels[homeId]?.dribbles}
      awayValue={stats.duels[awayId]?.dribbles}
    />
    <StatRow
      label="Dribbles Success"
      homeValue={stats.duels[homeId]?.dribblesSuccessful}
      awayValue={stats.duels[awayId]?.dribblesSuccessful}
    />
    <StatRow
      label="Challenge Intensity"
      homeValue={stats.duels[homeId]?.challengeIntensity}
      awayValue={stats.duels[awayId]?.challengeIntensity}
      format="decimal"
    />
  </div>
);

export const AttacksSection = ({ stats, homeId, awayId }: SectionProps) => (
  <div className="mb-4 px-2">
    <StatRow
      label="Total Attacks"
      homeValue={stats.attacks[homeId]?.total}
      awayValue={stats.attacks[awayId]?.total}
    />
    <StatRow
      label="With Shots"
      homeValue={stats.attacks[homeId]?.withShots}
      awayValue={stats.attacks[awayId]?.withShots}
    />
    <StatRow
      label="Positional"
      homeValue={stats.attacks[homeId]?.positionalAttack}
      awayValue={stats.attacks[awayId]?.positionalAttack}
    />
    <StatRow
      label="Positional w/ Shots"
      homeValue={stats.attacks[homeId]?.positionalWithShots}
      awayValue={stats.attacks[awayId]?.positionalWithShots}
    />
    <StatRow
      label="Counter Attacks"
      homeValue={stats.attacks[homeId]?.counterAttacks}
      awayValue={stats.attacks[awayId]?.counterAttacks}
    />
    <StatRow
      label="From Corners"
      homeValue={stats.attacks[homeId]?.corners}
      awayValue={stats.attacks[awayId]?.corners}
    />
    <StatRow
      label="From Free Kicks"
      homeValue={stats.attacks[homeId]?.freeKicks}
      awayValue={stats.attacks[awayId]?.freeKicks}
    />
  </div>
);

export const FlanksSection = ({ stats, homeId, awayId }: SectionProps) => (
  <div className="mb-4 px-2">
    <StatRow
      label="Left Attacks"
      homeValue={stats.flanks[homeId]?.leftFlankAttacks}
      awayValue={stats.flanks[awayId]?.leftFlankAttacks}
    />
    <StatRow
      label="Left xG"
      homeValue={stats.flanks[homeId]?.leftFlankXg}
      awayValue={stats.flanks[awayId]?.leftFlankXg}
      format="decimal"
    />
    <StatRow
      label="Center Attacks"
      homeValue={stats.flanks[homeId]?.centerAttacks}
      awayValue={stats.flanks[awayId]?.centerAttacks}
    />
    <StatRow
      label="Center xG"
      homeValue={stats.flanks[homeId]?.centerXg}
      awayValue={stats.flanks[awayId]?.centerXg}
      format="decimal"
    />
    <StatRow
      label="Right Attacks"
      homeValue={stats.flanks[homeId]?.rightFlankAttacks}
      awayValue={stats.flanks[awayId]?.rightFlankAttacks}
    />
    <StatRow
      label="Right xG"
      homeValue={stats.flanks[homeId]?.rightFlankXg}
      awayValue={stats.flanks[awayId]?.rightFlankXg}
      format="decimal"
    />
  </div>
);
